import { useEffect, useRef, useState } from "react"
import { useRecoilState, useRecoilValue } from "recoil"
import { useSearchParams } from "react-router-dom"
import { CaretDown, CaretUp } from "@carbon/icons-react"
import { selAggLayerState, checkboxState, geoState, selSubRegionState, poiMapColorState } from "../../states"
import { AggLayerPopup } from "./AggLayerPopup"
import { Typography } from "../Shadcn/Typography"

export const AggUnitControl = () => {
    const [selAggLayer, setSelAggLayer] = useRecoilState(selAggLayerState)
    const isChecked = useRecoilValue(checkboxState)
    const selectedGeo = useRecoilValue(geoState)
    const selSubRegion = useRecoilValue(selSubRegionState)
    const poiColors = useRecoilValue(poiMapColorState)
    const [searchParams] = useSearchParams()
    const catchmentId = searchParams.get("catchment")

    const [open, setOpen] = useState(false)
    const [popupPos, setPopupPos] = useState({ top: 0, left: 0 })
    const buttonRef = useRef<HTMLDivElement>(null)
    const popupRef = useRef<HTMLDivElement>(null)

    const isHexAvailable = poiColors?.rootProperties?.isHexAvailable !== false
    const catchmentOption = { label: "Catchment", value: selSubRegion, key: "catchment" }
    const hexGridOption = {
        label: "Hex Grid",
        value: { key: "hexgrid", title: "Hex Grid" },
        key: "hexgrid",
        disabled: !isHexAvailable,
    }

    const options = [
        ...((isChecked?.catchments && !catchmentId)
            ? [catchmentOption]
            : (((!selectedGeo.village || (selectedGeo?.village && !!catchmentId)) && isChecked?.subregionBoundaries && selSubRegion)
                ? [{ label: selSubRegion.title, value: selSubRegion, key: "subregion" }]
                : [])),
        ...(((!selectedGeo.village && selSubRegion?.key >= 4) || selectedGeo.village) ? [hexGridOption] : []),
        { label: "OFF", value: null, key: "off" },
    ]

    const selectedKey = !selAggLayer
        ? "off"
        : selAggLayer?.key === "hexgrid"
        ? "hexgrid"
        : isChecked?.catchments && !catchmentId
        ? "catchment"
        : "subregion"

    const displayTitle = selAggLayer
        ? (isChecked?.catchments && !catchmentId && selAggLayer?.key !== "hexgrid"
            ? "Catchment"
            : selAggLayer.title)
        : "Off"

    const handleOpen = () => {
        if (!buttonRef.current) return
        const rect = buttonRef.current.getBoundingClientRect()
        setPopupPos({ top: rect.bottom + 6, left: rect.left })
        setOpen((prev) => !prev)
    }

    const handleSelect = (value: any) => {
        setSelAggLayer(value)
        localStorage.setItem("agg-layer", JSON.stringify(value))
        setOpen(false)
    }

    useEffect(() => {
        if (!open) return
        const handleOutside = (e: MouseEvent) => {
            if (
                popupRef.current &&
                !popupRef.current.contains(e.target as Node) &&
                buttonRef.current &&
                !buttonRef.current.contains(e.target as Node)
            ) {
                setOpen(false)
            }
        }
        document.addEventListener("mousedown", handleOutside)
        return () => document.removeEventListener("mousedown", handleOutside)
    }, [open])

    const isVisible = (!selectedGeo.village && selSubRegion?.title) || selectedGeo.village
    if (!isVisible) return null

    return (
        <>
            <div
                ref={buttonRef}
                className="agg-unit-control absolute top-3 left-[1.185%] z-20 bg-white rounded-md shadow-md cursor-pointer flex flex-col justify-center items-center gap-2 px-2 py-2 border border-muted"
                onClick={handleOpen}
            >
                <Typography
                    variant="b3"
                    className="agg-unit-control__label font-mono text-muted-foreground uppercase tracking-widest"
                >
                    Aggregation Unit
                </Typography>
                <div className="agg-unit-control__value flex items-center justify-between gap-3 bg-muted rounded px-6 py-1">
                    <Typography variant="b3" className="font-mono font-medium text-foreground uppercase tracking-wide">
                        {displayTitle}
                    </Typography>
                    {open ? <CaretUp size={16} className="text-muted-foreground shrink-0" /> : <CaretDown size={16} className="text-muted-foreground shrink-0" />}
                </div>
            </div>

            {open && (
                <AggLayerPopup
                    ref={popupRef}
                    options={options}
                    selectedKey={selectedKey}
                    position={popupPos}
                    onSelect={handleSelect}
                />
            )}
        </>
    )
}
